import { useState, useCallback } from 'react';
import { budgets as budgetsApi } from '@/lib/api';
import type { Budget, CreateBudgetData, UpdateBudgetData } from '@/lib/types';

interface UseBudgetsReturn {
  budgets: Budget[];
  loading: boolean;
  error: string | null;
  fetchBudgets: (month?: number, year?: number) => Promise<void>;
  createBudget: (data: CreateBudgetData) => Promise<Budget>;
  updateBudget: (id: string, data: UpdateBudgetData) => Promise<Budget>;
  deleteBudget: (id: string) => Promise<void>;
}

export function useBudgets(): UseBudgetsReturn {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<number | undefined>();
  const [currentYear, setCurrentYear] = useState<number | undefined>();

  const fetchBudgets = useCallback(
    async (month?: number, year?: number) => {
      setLoading(true);
      setError(null);
      setCurrentMonth(month);
      setCurrentYear(year);

      try {
        const data = await budgetsApi.list(month, year);
        setBudgets(data);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch budgets';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createBudget = useCallback(
    async (data: CreateBudgetData): Promise<Budget> => {
      setError(null);
      try {
        const budget = await budgetsApi.create(data);
        await fetchBudgets(currentMonth, currentYear);
        return budget;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create budget';
        setError(message);
        throw err;
      }
    },
    [fetchBudgets, currentMonth, currentYear]
  );

  const updateBudget = useCallback(
    async (id: string, data: UpdateBudgetData): Promise<Budget> => {
      setError(null);
      try {
        const budget = await budgetsApi.update(id, data);
        await fetchBudgets(currentMonth, currentYear);
        return budget;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update budget';
        setError(message);
        throw err;
      }
    },
    [fetchBudgets, currentMonth, currentYear]
  );

  const deleteBudget = useCallback(
    async (id: string): Promise<void> => {
      setError(null);
      try {
        await budgetsApi.delete(id);
        await fetchBudgets(currentMonth, currentYear);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete budget';
        setError(message);
        throw err;
      }
    },
    [fetchBudgets, currentMonth, currentYear]
  );

  return {
    budgets,
    loading,
    error,
    fetchBudgets,
    createBudget,
    updateBudget,
    deleteBudget,
  };
}
